/**
 * OpenLigaDB API Service
 * For importing Bundesliga data into VoyagePlanner
 */

import { getToken } from '../utils/auth';

const _configured = (import.meta as any).env?.VITE_API_BASE as string | undefined;
const API_BASE_URL = _configured && /^https?:\/\//i.test(_configured) ? _configured : window.location.origin;

// Types for OpenLigaDB
export interface OpenLigaTeam {
  teamId: number;
  teamName: string;
  shortName: string;
  teamIconUrl?: string;
}

export interface OpenLigaGroup {
  groupName: string;
  groupOrderId: number;
  groupId: number;
}

export interface OpenLigaMatch {
  matchId: number;
  matchDateTime: string;
  matchDateTimeUtc: string;
  leagueId: number;
  leagueName: string;
  leagueSeason: number;
  leagueShortcut: string;
  matchIsFinished: boolean;
  group?: OpenLigaGroup;
  team1?: OpenLigaTeam;
  team2?: OpenLigaTeam;
}

export interface OpenLigaLeague {
  leagueId: number;
  leagueName: string;
  leagueShortcut: string;
  leagueSeason: string;
}

export interface OpenLigaImportConfig {
  leagueShortcut: string;
  season: number;
  matchdayNumber?: number;
  updateExisting: boolean;
  createMissingClubs: boolean;
  createMissingMatchdays: boolean;
  filterByClubName?: string; // Only import matches involving this club
}

export interface OpenLigaImportResult {
  success: boolean;
  message: string;
  matchesImported: number;
  matchesUpdated: number;
  matchesSkipped: number;
  clubsCreated: number;
  matchdaysCreated: number;
  errors: string[];
  warnings: string[];
  importedAt: string;
}

// Supported leagues
export const SUPPORTED_LEAGUES = [
  { shortcut: 'bl1', name: '1. Bundesliga', level: 1 },
  { shortcut: 'bl2', name: '2. Bundesliga', level: 2 },
  { shortcut: 'bl3', name: '3. Liga', level: 3 },
  { shortcut: 'dfb', name: 'DFB-Pokal', level: 0 },
] as const;

// Helper function for authenticated requests
const makeAuthenticatedRequest = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  const token = getToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}/api${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API Error ${response.status}: ${errorText}`);
  }

  const text = await response.text();
  if (!text || text.trim() === '') {
    return undefined as T;
  }

  return JSON.parse(text);
};

export const openLigaDbApi = {
  /**
   * Get available leagues from OpenLigaDB
   */
  getLeagues: async (): Promise<OpenLigaLeague[]> => {
    return makeAuthenticatedRequest('/openligadb/leagues');
  },

  /**
   * Get teams for a league/season
   */
  getTeams: async (leagueShortcut: string, season: number): Promise<OpenLigaTeam[]> => {
    return makeAuthenticatedRequest(`/openligadb/teams/${leagueShortcut}/${season}`);
  },

  /**
   * Get matches for a league/season (optionally specific matchday)
   */
  getMatches: async (
    leagueShortcut: string,
    season: number,
    matchday?: number
  ): Promise<OpenLigaMatch[]> => {
    const url = matchday
      ? `/openligadb/matches/${leagueShortcut}/${season}?matchday=${matchday}`
      : `/openligadb/matches/${leagueShortcut}/${season}`;
    return makeAuthenticatedRequest(url);
  },

  /**
   * Preview import without making changes
   */
  previewImport: async (config: OpenLigaImportConfig): Promise<OpenLigaImportResult> => {
    return makeAuthenticatedRequest('/openligadb/preview', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  },

  /**
   * Import matches from OpenLigaDB
   */
  importMatches: async (config: OpenLigaImportConfig): Promise<OpenLigaImportResult> => {
    return makeAuthenticatedRequest('/openligadb/import', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  },

  /**
   * Import teams only
   */
  importTeams: async (leagueShortcut: string, season: number): Promise<OpenLigaImportResult> => {
    return makeAuthenticatedRequest(`/openligadb/import-teams/${leagueShortcut}/${season}`, {
      method: 'POST',
    });
  },
};

